import React from 'react';

import {useFocusOnElement} from '@optimaxdev/analytics/desktop/hooks/useFocusOnElement/useFocusOnElement';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Colors, CtaButton} from '@optimaxdev/design-system';
import {Icon, KlarnaBadge} from '@optimaxdev/icons';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';

import {CompleteOrderType} from 'types/completeOrderType';

import {PAYMENT_CODE_MESSAGE} from '../../constants/checkout';
import {PAYMENTS} from '../../constants/paymentStep';
import {setPayerOptions} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {
    getCompleteOrderResult,
    getIsPlacingOrder,
} from '../../store/selectors/completeOrder/completeOrder';
import {getNameSelectedMethod} from '../../store/selectors/paymentStep/paymentStep';

import {PaymentStepAgreement} from '../paymentStepAgreement/paymentStepAgreement';
import {PlaceOrderErrors} from '../placeOrderErrors/placeOrderErrors';

import s from './placeOrderForKlarna.scss';

export type PropsType = {
    isLoading: boolean;
    onSubmit?: () => void;
    completeOrder: CompleteOrderType;
    namePaymentMethod: PAYMENTS;
    isPlacingOrder: boolean;
};

/**
 * Place order for Klarna for checkout
 *
 * @constructor
 */
export const PlaceOrderForKlarna: React.FC<PropsType> = ({
    completeOrder,
    isLoading,
    isPlacingOrder,
    namePaymentMethod,
    onSubmit = () => {},
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const sendAnalyticsCallback = () => {
        const paymentName = PAYMENT_CODE_MESSAGE[namePaymentMethod]
            ? PAYMENT_CODE_MESSAGE[namePaymentMethod]
            : 'Other';

        sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
            message: `CTA - Place Order - ${paymentName} - Hover`,
            method: 'Payment',
        });
    };

    const onFormSend = () => {
        if (onSubmit) onSubmit();
    };

    const {handler, ref} = useFocusOnElement({
        callBack: sendAnalyticsCallback,
    });

    const errors = [...((completeOrder.error && completeOrder.errors) || [])];

    return (
        <>
            <section className={classNames(s.wrapper, {[s.wrapperFacelift]: isFacelift})}>
                <span ref={ref}>
                    <CtaButton
                        ariaLabel="Place Order"
                        className={s.button}
                        isDisabled={errors.length > 0 || isLoading}
                        isLoading={isLoading || isPlacingOrder}
                        onClick={onFormSend}
                        onHover={handler}
                        primaryColor={Colors.codGray}
                        secondaryColor={Colors.tundora}
                        size="medium"
                        type="button"
                    >
                        <span className={s.buttonText}>
                            Pay With
                            <Icon className={s.icon} height={28} icon={KlarnaBadge} width={67} />
                        </span>
                    </CtaButton>
                </span>
            </section>

            <div className={classNames({[s.agreementWrapperFacelift]: isFacelift})}>
                <p className={classNames(s.text, {[s.textFaceliftTop]: isFacelift})}>
                    Click the button to be redirected to Klarna & complete your purchase.
                </p>
                <PaymentStepAgreement
                    className={classNames(s.text, {[s.textFaceliftBottom]: isFacelift})}
                    isKlarna
                />
            </div>

            {errors.length > 0 && <PlaceOrderErrors errors={errors} />}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    completeOrder: getCompleteOrderResult(store),
    isPlacingOrder: getIsPlacingOrder(store),
    namePaymentMethod: getNameSelectedMethod(store),
});

const mapDispatchToProps = {
    changeSubscribeOption: setPayerOptions,
};

export const PlaceOrderForKlarnaConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PlaceOrderForKlarna);
